import type { TechnologyBranch, TechnologyId } from './technologies'

// IDs are save-facing identifiers. Do not rename the original two powers.
export const MAJOR_POWER_IDS = [
  'starsea',
  'redplain',
  'zhonghua',
  'britannia',
  'francia',
] as const
export type MajorPowerId = (typeof MAJOR_POWER_IDS)[number]

// The current scenario deliberately has only two participants.
export const ACTIVE_MAJOR_POWER_IDS = ['starsea', 'redplain'] as const
export type ActiveMajorPowerId = (typeof ACTIVE_MAJOR_POWER_IDS)[number]

export type ClientNationId =
  | 'isles-republic'
  | 'southcape-commonwealth'
  | 'northsea-republic'
  | 'steppe-union'
  | 'jade-coast-commonwealth'
  | 'oceanic-commonwealth'
  | 'equator-commonwealth'
export type MinorNationId =
  | 'highland-republic'
  | 'strait-free-state'
export type NationId = MajorPowerId | ClientNationId | MinorNationId
export type NationKind = 'major' | 'minor' | 'client'
export type NationAvailability = 'active' | 'planned'

export interface NationTechProfile {
  focusBranches: readonly TechnologyBranch[]
  preferredTechnologies: readonly TechnologyId[]
  access: 'independent' | 'patron-shared'
}

export interface NationAiConfig {
  enabled: boolean
  strategyId: MajorPowerId | null
}

export interface NationDefinition<Id extends string = string> {
  id: Id
  displayName: string
  kind: NationKind
  availability: NationAvailability
  patronId: MajorPowerId | null
  route: {
    name: string
    summary: string
    traitSlots: number
  }
  techProfile: NationTechProfile
  ai: NationAiConfig
}

export const NATION_DEFINITIONS: Record<NationId, NationDefinition> = {
  starsea: {
    id: 'starsea', displayName: '阿美丽卡联邦', kind: 'major',
    availability: 'active', patronId: null,
    route: { name: '系统工程与电子集成', summary: '电子工业、科研组织与复杂航天系统集成。', traitSlots: 2 },
    techProfile: { focusBranches: ['electronics', 'spaceflight'], preferredTechnologies: ['vacuum-electronics', 'radar-tracking', 'life-support'], access: 'independent' },
    ai: { enabled: true, strategyId: 'starsea' },
  },
  redplain: {
    id: 'redplain', displayName: '苏维埃联盟', kind: 'major',
    availability: 'active', patronId: null,
    route: { name: '重型运载与早期突破', summary: '重工业、燃料供给与早期航天里程碑。', traitSlots: 2 },
    techProfile: { focusBranches: ['industry', 'rocketry'], preferredTechnologies: ['industrial-standardization', 'liquid-rocketry', 'multistage-rockets'], access: 'independent' },
    ai: { enabled: true, strategyId: 'redplain' },
  },
  zhonghua: {
    id: 'zhonghua', displayName: '中华共和国', kind: 'major',
    availability: 'planned', patronId: null,
    route: { name: '工业协同与渐进航天', summary: '标准化制造与科研协同支撑逐步扩展的载人航天。', traitSlots: 2 },
    techProfile: { focusBranches: ['industry', 'spaceflight'], preferredTechnologies: ['industrial-standardization', 'life-support', 'orbital-flight'], access: 'independent' },
    ai: { enabled: false, strategyId: 'zhonghua' },
  },
  britannia: {
    id: 'britannia', displayName: '英吉利联合王国', kind: 'major',
    availability: 'planned', patronId: null,
    route: { name: '航空电子与精密测控', summary: '航空电子和测控网络支持卫星与远程任务。', traitSlots: 2 },
    techProfile: { focusBranches: ['aviation', 'electronics'], preferredTechnologies: ['jet-propulsion', 'radar-tracking', 'inertial-guidance'], access: 'independent' },
    ai: { enabled: false, strategyId: 'britannia' },
  },
  francia: {
    id: 'francia', displayName: '法兰西共和国', kind: 'major',
    availability: 'planned', patronId: null,
    route: { name: '独立科研与推进技术', summary: '推进试验与自主发射能力带动航天科研。', traitSlots: 2 },
    techProfile: { focusBranches: ['rocketry', 'electronics'], preferredTechnologies: ['liquid-rocketry', 'multistage-rockets', 'inertial-guidance'], access: 'independent' },
    ai: { enabled: false, strategyId: 'francia' },
  },
  'isles-republic': {
    id: 'isles-republic', displayName: '群岛共和国', kind: 'client',
    availability: 'active', patronId: 'starsea',
    route: { name: '电子工业', summary: '当前原型中的附属国。', traitSlots: 0 },
    techProfile: { focusBranches: ['electronics'], preferredTechnologies: [], access: 'patron-shared' },
    ai: { enabled: false, strategyId: null },
  },
  'southcape-commonwealth': {
    id: 'southcape-commonwealth', displayName: '南岬共同体', kind: 'client',
    availability: 'active', patronId: 'starsea',
    route: { name: '燃料供给', summary: '当前原型中的附属国。', traitSlots: 0 },
    techProfile: { focusBranches: ['industry'], preferredTechnologies: [], access: 'patron-shared' },
    ai: { enabled: false, strategyId: null },
  },
  'northsea-republic': {
    id: 'northsea-republic', displayName: '北海人民共和国', kind: 'client',
    availability: 'active', patronId: 'redplain',
    route: { name: '矿业供给', summary: '当前原型中的附属国。', traitSlots: 0 },
    techProfile: { focusBranches: ['industry'], preferredTechnologies: [], access: 'patron-shared' },
    ai: { enabled: false, strategyId: null },
  },
  'steppe-union': {
    id: 'steppe-union', displayName: '草原联盟国', kind: 'client',
    availability: 'active', patronId: 'redplain',
    route: { name: '燃料供给', summary: '当前原型中的附属国。', traitSlots: 0 },
    techProfile: { focusBranches: ['industry'], preferredTechnologies: [], access: 'patron-shared' },
    ai: { enabled: false, strategyId: null },
  },
  'jade-coast-commonwealth': {
    id: 'jade-coast-commonwealth', displayName: '玉海共同体', kind: 'client',
    availability: 'planned', patronId: 'zhonghua',
    route: { name: '沿海制造与电子协作', summary: '未来世界剧本中的附属国模板。', traitSlots: 0 },
    techProfile: { focusBranches: ['industry', 'electronics'], preferredTechnologies: [], access: 'patron-shared' },
    ai: { enabled: false, strategyId: null },
  },
  'oceanic-commonwealth': {
    id: 'oceanic-commonwealth', displayName: '远洋共同体', kind: 'client',
    availability: 'planned', patronId: 'britannia',
    route: { name: '远洋基地与燃料补给', summary: '未来世界剧本中的附属国模板。', traitSlots: 0 },
    techProfile: { focusBranches: ['aviation', 'industry'], preferredTechnologies: [], access: 'patron-shared' },
    ai: { enabled: false, strategyId: null },
  },
  'equator-commonwealth': {
    id: 'equator-commonwealth', displayName: '赤道共同体', kind: 'client',
    availability: 'planned', patronId: 'francia',
    route: { name: '赤道资源与发射支援', summary: '未来世界剧本中的附属国模板。', traitSlots: 0 },
    techProfile: { focusBranches: ['industry', 'rocketry'], preferredTechnologies: [], access: 'patron-shared' },
    ai: { enabled: false, strategyId: null },
  },
  'highland-republic': {
    id: 'highland-republic', displayName: '高地共和国', kind: 'minor',
    availability: 'planned', patronId: null,
    route: { name: '矿产与通用工业', summary: '未来世界剧本中的独立小国模板。', traitSlots: 0 },
    techProfile: { focusBranches: ['industry'], preferredTechnologies: [], access: 'independent' },
    ai: { enabled: false, strategyId: null },
  },
  'strait-free-state': {
    id: 'strait-free-state', displayName: '海峡自由国', kind: 'minor',
    availability: 'planned', patronId: null,
    route: { name: '贸易与电子工业', summary: '未来世界剧本中的独立小国模板。', traitSlots: 0 },
    techProfile: { focusBranches: ['electronics', 'aviation'], preferredTechnologies: [], access: 'independent' },
    ai: { enabled: false, strategyId: null },
  },
}

export function getNationDefinition<Id extends NationId>(id: Id): NationDefinition<Id> {
  return NATION_DEFINITIONS[id] as NationDefinition<Id>
}

export function isMajorPowerId(value: unknown): value is MajorPowerId {
  return typeof value === 'string' && MAJOR_POWER_IDS.some((id) => id === value)
}

export function isActiveMajorPowerId(value: unknown): value is ActiveMajorPowerId {
  return typeof value === 'string' && ACTIVE_MAJOR_POWER_IDS.some((id) => id === value)
}
